import type { Conversation } from "@/types/inbox.types";
import { DEMO_CONTRACT_IDS } from "@/mockData/demoContractData";

export const mockConversations: Conversation[] = [
  {
    id: "conv-1",
    subject: "Artist Recording Deal - Signature Required",
    preview: "Legal Team: The contract is ready for your final review and signature...",
    participants: ["Demo User", "Legal Team", "Adam Silver", "Roger Goodell"],
    status: "unread",
    priority: "high",
    timestamp: "2025-01-08T10:30:00Z",
    relativeTime: "2 hours ago",
    messageCount: 5,
    unreadCount: 2,
    type: "contract",
    actionRequired: true,
    metadata: {
      entityId: DEMO_CONTRACT_IDS.ARTIST_RECORDING_DEAL,
      entityName: "Artist Recording Deal",
      tags: ["urgent", "signature", "recording"],
      relatedItems: [
        { id: "deal-1", name: "Sony Music Partnership", type: "deal" },
        { id: "doc-1", name: "Master Agreement", type: "document" },
      ],
    },
    messages: [
      {
        id: "msg-1",
        author: "Adam Silver",
        content:
          "Hey team, I've reviewed the initial draft of the Artist Recording Deal. Overall looks good, but I have a few questions about the royalty split in Section 4.2.",
        timestamp: "2025-01-07T14:00:00Z",
        relativeTime: "Yesterday",
        attachments: [{ name: "Initial_Review_Notes.pdf", size: "245 KB", type: "pdf" }],
      },
      {
        id: "msg-2",
        author: "Legal Team",
        content:
          "Thanks Adam. I've updated Section 4.2 based on your feedback. The royalty structure now clearly outlines the 70/30 split we discussed, with provisions for digital streaming vs physical sales.",
        timestamp: "2025-01-07T16:30:00Z",
        relativeTime: "Yesterday",
      },
      {
        id: "msg-3",
        author: "You",
        isCurrentUser: true,
        content:
          "This looks much better. Can we also add the territory restrictions we talked about last week?",
        timestamp: "2025-01-07T17:15:00Z",
        relativeTime: "Yesterday",
        reactions: [{ emoji: "👍", count: 2, userReacted: false }],
      },
      {
        id: "msg-4",
        author: "Legal Team",
        content:
          "Absolutely. I've added Section 7 to cover territory restrictions. The contract now explicitly covers North America and Europe, with separate negotiation required for APAC territories.",
        timestamp: "2025-01-08T09:00:00Z",
        relativeTime: "3 hours ago",
        attachments: [
          { name: "Artist_Recording_Deal_v2.pdf", size: "2.4 MB", type: "pdf" },
          { name: "Territory_Exhibit.pdf", size: "856 KB", type: "pdf" },
        ],
      },
      {
        id: "msg-5",
        author: "Legal Team",
        content:
          "The contract is ready for your final review and signature. Please review Section 4.2 (royalties), Section 7 (territories), and the updated payment schedule in Section 9. Deadline for signature is December 20th.",
        timestamp: "2025-01-08T10:30:00Z",
        relativeTime: "2 hours ago",
        reactions: [{ emoji: "🎯", count: 1, userReacted: false }],
      },
    ],
  },
  {
    id: "conv-2",
    subject: "Sony Music Partnership - Deal Progress Update",
    preview: "Adam Silver: Great news! We've moved to the next stage...",
    participants: ["Demo User", "Adam Silver", "Roger Goodell"],
    status: "unread",
    priority: "medium",
    timestamp: "2025-01-08T09:15:00Z",
    relativeTime: "3 hours ago",
    messageCount: 3,
    unreadCount: 1,
    type: "deal",
    metadata: {
      entityId: "deal-1",
      entityName: "Sony Music Partnership",
      tags: ["partnership", "in-progress"],
      relatedItems: [
        {
          id: DEMO_CONTRACT_IDS.ARTIST_RECORDING_DEAL,
          name: "Artist Recording Deal",
          type: "contract",
        },
      ],
    },
    messages: [
      {
        id: "msg-1",
        author: "Roger Goodell",
        content:
          "I've connected with the Sony team and they're excited to move forward. They're requesting some additional financial projections for Q1-Q2.",
        timestamp: "2025-01-07T11:00:00Z",
        relativeTime: "Yesterday",
      },
      {
        id: "msg-2",
        author: "You",
        isCurrentUser: true,
        content:
          "That's great news! I can pull together those projections by end of day. Should I include the streaming metrics as well?",
        timestamp: "2025-01-07T14:30:00Z",
        relativeTime: "Yesterday",
        reactions: [{ emoji: "👍", count: 2, userReacted: false }],
      },
      {
        id: "msg-3",
        author: "Adam Silver",
        content:
          "Great news! We've moved to the next stage. Sony has approved the initial terms and we're now in active negotiations. The deal is officially marked as In Progress. Next steps: finalize the recording contract and set up the first studio session.",
        timestamp: "2025-01-08T09:15:00Z",
        relativeTime: "3 hours ago",
      },
    ],
  },
  {
    id: "conv-3",
    subject: "Amendment #1 - Review Requested",
    preview: "Legal Team: I've shared Amendment #1 for your review...",
    participants: ["Demo User", "Legal Team", "Adam Silver"],
    status: "unread",
    priority: "high",
    timestamp: "2025-01-08T08:00:00Z",
    relativeTime: "5 hours ago",
    messageCount: 2,
    unreadCount: 2,
    type: "document",
    actionRequired: true,
    metadata: {
      entityId: "doc-2",
      entityName: "Amendment #1",
      tags: ["amendment", "review"],
      relatedItems: [
        {
          id: DEMO_CONTRACT_IDS.ARTIST_RECORDING_DEAL,
          name: "Artist Recording Deal",
          type: "contract",
        },
      ],
    },
    messages: [
      {
        id: "msg-1",
        author: "Legal Team",
        content:
          "I've shared Amendment #1 for your review. This amendment addresses the payment schedule changes we discussed last week. Key changes are in Section 3 and Section 5.",
        timestamp: "2025-01-08T08:00:00Z",
        relativeTime: "5 hours ago",
        attachments: [{ name: "Amendment_001.pdf", size: "1.2 MB", type: "pdf" }],
      },
      {
        id: "msg-2",
        author: "Legal Team",
        content:
          "Please review by EOD tomorrow if possible. Adam will need to approve as well before we can proceed with execution.",
        timestamp: "2025-01-08T08:05:00Z",
        relativeTime: "5 hours ago",
      },
    ],
  },
  {
    id: "conv-4",
    subject: "Producer Agreement - Analysis Complete",
    preview: "AI Analysis: Your contract analysis is complete...",
    participants: ["Demo User", "Roger Goodell"],
    status: "unread",
    priority: "low",
    timestamp: "2025-01-07T16:30:00Z",
    relativeTime: "Yesterday",
    messageCount: 2,
    unreadCount: 1,
    type: "contract",
    metadata: {
      entityId: DEMO_CONTRACT_IDS.PRODUCER_AGREEMENT,
      entityName: "Producer Agreement",
      tags: ["analysis", "producer"],
      relatedItems: [{ id: "deal-2", name: "Netflix Sync License", type: "deal" }],
    },
    messages: [
      {
        id: "msg-1",
        author: "System",
        content:
          "Your contract analysis is complete. We've identified 3 key terms, 2 potential concerns, and 5 action items. Click to view the full analysis report.",
        timestamp: "2025-01-07T16:30:00Z",
        relativeTime: "Yesterday",
      },
      {
        id: "msg-2",
        author: "Roger Goodell",
        content:
          "I took a quick look at the analysis. The payment terms look favorable. Let's discuss the IP ownership clause in our next meeting.",
        timestamp: "2025-01-07T17:00:00Z",
        relativeTime: "Yesterday",
      },
    ],
  },
  {
    id: "conv-5",
    subject: "Netflix Sync License - New Comments",
    preview: "Roger Goodell: I've reviewed the latest version and have some thoughts...",
    participants: ["Demo User", "Roger Goodell", "Adam Silver"],
    status: "read",
    priority: "medium",
    timestamp: "2025-01-07T14:00:00Z",
    relativeTime: "Yesterday",
    messageCount: 4,
    unreadCount: 0,
    type: "deal",
    metadata: {
      entityId: "deal-2",
      entityName: "Netflix Sync License",
      tags: ["comment", "sync-license"],
      relatedItems: [
        {
          id: DEMO_CONTRACT_IDS.PRODUCER_AGREEMENT,
          name: "Producer Agreement",
          type: "contract",
        },
      ],
    },
    messages: [
      {
        id: "msg-1",
        author: "You",
        isCurrentUser: true,
        content:
          "Netflix has come back with their proposed sync license terms. The fee structure is better than expected, but the exclusivity period is longer than we discussed.",
        timestamp: "2025-01-07T10:00:00Z",
        relativeTime: "Yesterday",
        attachments: [{ name: "Netflix_Proposal.pdf", size: "1.8 MB", type: "pdf" }],
      },
      {
        id: "msg-2",
        author: "Roger Goodell",
        content:
          "I've reviewed the latest version and have some thoughts on the exclusivity clause. Can we negotiate down to 18 months instead of 24?",
        timestamp: "2025-01-07T14:00:00Z",
        relativeTime: "Yesterday",
        reactions: [
          { emoji: "🤔", count: 1, userReacted: true },
          { emoji: "👍", count: 1, userReacted: false },
        ],
      },
      {
        id: "msg-3",
        author: "Adam Silver",
        content:
          "Agreed with Roger. The fee is good but 24 months is too restrictive. Let's counter with 18 months and see how they respond.",
        timestamp: "2025-01-07T14:30:00Z",
        relativeTime: "Yesterday",
      },
      {
        id: "msg-4",
        author: "You",
        isCurrentUser: true,
        content:
          "Sounds good. I'll reach out to their team today and propose the 18-month timeline. Will keep you both updated.",
        timestamp: "2025-01-07T15:00:00Z",
        relativeTime: "Yesterday",
        reactions: [{ emoji: "🎯", count: 2, userReacted: false }],
      },
    ],
  },
  {
    id: "conv-6",
    subject: "Brand Partnership / NIL Deal - Expiring Soon",
    preview: "System: Your contract expires in 30 days...",
    participants: ["Demo User", "Adam Silver"],
    status: "read",
    priority: "medium",
    timestamp: "2025-01-05T09:00:00Z",
    relativeTime: "3 days ago",
    messageCount: 3,
    unreadCount: 0,
    type: "contract",
    actionRequired: true,
    metadata: {
      entityId: DEMO_CONTRACT_IDS.BRAND_PARTNERSHIP_NIL,
      entityName: "Brand Partnership / NIL Deal",
      tags: ["expiring", "brand-partnership"],
      relatedItems: [],
    },
    messages: [
      {
        id: "msg-1",
        author: "System",
        content:
          "Your contract expires in 30 days. Please review and decide if you'd like to renew or renegotiate terms.",
        timestamp: "2025-01-05T09:00:00Z",
        relativeTime: "3 days ago",
      },
      {
        id: "msg-2",
        author: "Adam Silver",
        content:
          "We should definitely look at renewing this one. The brand partnership has been performing well. Want to set up a call to discuss renewal terms?",
        timestamp: "2025-01-05T11:00:00Z",
        relativeTime: "3 days ago",
      },
      {
        id: "msg-3",
        author: "You",
        isCurrentUser: true,
        content:
          "Agreed! Let's schedule for next week. I'll prepare some updated metrics to help with the negotiation.",
        timestamp: "2025-01-05T14:00:00Z",
        relativeTime: "3 days ago",
      },
    ],
  },
];
